import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatError } from "../../lib/api";
import { Coins, Check, ArrowRight, Loader2, Receipt, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`}>{children}</div>
);

export default function CollegeBilling() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    api.get("/billing/me").then((r) => setMe(r.data));
    api.get("/billing/plans").then((r) => setPlans(r.data));
  }, []);
  if (!me || !plans) return <div className="min-h-[40vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const cur = plans.currency;
  const col = me.college || {};
  const remaining = col.credits_remaining ?? 0;
  const pctUsed = col.credits_purchased ? Math.round(100 * col.credits_used / col.credits_purchased) : 0;
  const low = col.credits_purchased && Math.round(100 * remaining / col.credits_purchased) <= 20;

  const contactSales = async () => {
    navigate("/pricing");
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-brand mb-1">Billing & Credits</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">College Subscription</h1>
      </div>

      {low && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3"><AlertTriangle className="h-5 w-5 text-amber-600" /><span className="text-sm font-semibold text-amber-800">Your credits are running low. Purchase more to keep sponsoring interviews.</span></div>
      )}

      {/* Credit summary */}
      <Card className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2"><Coins className="h-5 w-5 text-brand" /><span className="font-heading font-bold text-ink">Interview Credits</span>
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-brand-light text-brand capitalize">{col.plan || "—"} plan</span></div>
          <div className="text-sm num text-ink-soft">Expires {col.credits_expiry}</div>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div><div className="num font-bold text-2xl text-ink" data-testid="credits-purchased">{col.credits_purchased}</div><div className="text-xs text-ink-faint">Purchased</div></div>
          <div><div className="num font-bold text-2xl text-ink">{col.credits_used}</div><div className="text-xs text-ink-faint">Used</div></div>
          <div><div className="num font-bold text-2xl text-emerald-600" data-testid="credits-remaining">{remaining}</div><div className="text-xs text-ink-faint">Remaining</div></div>
        </div>
        <div className="h-3 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-brand" style={{ width: `${pctUsed}%` }} /></div>
      </Card>

      {/* Buy credits */}
      <div>
        <div className="font-heading font-bold text-ink mb-3">Purchase More Credits</div>
        <div className="grid md:grid-cols-3 gap-6 items-start">
          {plans.college_plans.map((p) => (
            <div key={p.id} className={`rounded-2xl p-6 border flex flex-col ${p.popular ? "border-brand bg-slate-900 text-white shadow-ambient" : "border-slate-200 bg-white"}`}>
              <div className={`font-bold ${p.popular ? "text-white" : "text-ink"}`}>{p.name}</div>
              <div className={`text-xs mb-3 ${p.popular ? "text-blue-200" : "text-ink-faint"}`}>{p.tagline}</div>
              <div className="num font-extrabold text-2xl mb-1">{p.price == null ? "Custom" : `${cur}${p.price.toLocaleString()}`}</div>
              {p.credits && <div className={`text-xs mb-4 ${p.popular ? "text-blue-200" : "text-ink-faint"}`}>{p.credits} interview credits</div>}
              <ul className="space-y-1.5 mb-5 flex-1">{p.features.slice(0, 5).map((f) => <li key={f} className={`flex items-center gap-2 text-xs ${p.popular ? "text-blue-50" : "text-ink-soft"}`}><Check className={`h-3.5 w-3.5 ${p.popular ? "text-white" : "text-emerald-500"}`} /> {f}</li>)}</ul>
              {p.type === "contact"
                ? <button data-testid="college-enterprise" onClick={contactSales} className="w-full text-sm font-bold px-4 py-2.5 rounded-full bg-slate-100 text-ink hover:bg-slate-200 transition-colors">Contact Sales</button>
                : <button data-testid={`buy-${p.id}`} onClick={() => navigate(`/checkout?kind=college_${p.id}`)} className={`w-full inline-flex items-center justify-center gap-2 text-sm font-bold px-4 py-2.5 rounded-full transition-colors ${p.popular ? "bg-white text-brand hover:bg-blue-50" : "bg-brand text-white hover:bg-brand-hover"}`}>Buy Credits <ArrowRight className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      </div>

      {/* History */}
      <Card className="p-6">
        <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><Receipt className="h-4 w-4 text-brand" /> Billing History</div>
        {me.invoices.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-faint border-b border-slate-200"><th className="px-3 py-2.5">Invoice</th><th className="px-3 py-2.5">Description</th><th className="px-3 py-2.5">Date</th><th className="px-3 py-2.5">Amount</th><th className="px-3 py-2.5 text-right"></th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {me.invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-${inv.id}`}>
                    <td className="px-3 py-3 num text-brand font-semibold">{inv.id}</td>
                    <td className="px-3 py-3 text-ink">{inv.description}</td>
                    <td className="px-3 py-3 num text-ink-soft">{new Date(inv.date).toLocaleDateString()}</td>
                    <td className="px-3 py-3 num font-bold text-ink">{inv.currency}{inv.amount.toLocaleString()}</td>
                    <td className="px-3 py-3 text-right"><Link to={`/invoice/${inv.id}`} className="text-xs font-bold text-brand">View</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="text-sm text-ink-soft py-6 text-center">No purchases yet.</div>}
      </Card>
    </div>
  );
}
